<template>
  <div class="min-h-screen bg-gray-100 flex flex-col">
    <Nav />

    <!-- ── Empty Cart ──────────────────────────────────────────────────── -->
    <div v-if="cartItems.length === 0" class="flex-1 flex flex-col items-center justify-center py-20 px-4">
      <img src="/shopping-cart-304843_640.webp" alt="Empty Cart" class="w-40 h-auto mb-6 opacity-70" />
      <h1 class="font-bold text-2xl text-gray-700 mb-2">Your Cart is empty</h1>
      <p class="text-gray-400 mb-6">Looks like you haven't added anything yet.</p>
      <router-link
        to="/"
        class="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold px-8 py-3 rounded-full shadow transition-colors"
      >
        Continue Shopping
      </router-link>
    </div>

    <!-- ── Cart with items ─────────────────────────────────────────────── -->
    <div v-else class="flex-1 max-w-screen-xl mx-auto w-full px-4 py-8">
      <h1 class="text-2xl font-bold text-gray-800 mb-6 border-b pb-4">
        Shopping Cart
        <span class="text-base font-normal text-gray-400 ml-2">({{ store.cartNumber }} {{ store.cartNumber === 1 ? 'item' : 'items' }})</span>
      </h1>

      <div class="flex flex-col lg:flex-row gap-8">

        <!-- ── Item List ───────────────────────────────────────────────── -->
        <div class="flex-1 space-y-4">
          <div
            v-for="(item, index) in cartItems"
            :key="index"
            class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col sm:flex-row gap-4 hover:shadow-md transition-shadow"
          >
            <!-- Image -->
            <div class="w-full sm:w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
              <img
                v-if="item.image"
                :src="item.image"
                :alt="item.name"
                class="w-full h-full object-cover"
              />
              <i v-else class="fa-solid fa-box text-4xl text-gray-300"></i>
            </div>

            <!-- Details -->
            <div class="flex-1 flex flex-col justify-between">
              <div>
                <p class="font-semibold text-gray-800 text-sm leading-snug mb-1 line-clamp-2">{{ item.name }}</p>
                <p class="text-green-600 font-bold text-base">${{ item.price.toFixed(2) }}</p>
                <p class="text-xs text-green-500 mt-0.5">In Stock · Free Delivery</p>
              </div>

              <!-- Qty + Remove -->
              <div class="flex items-center gap-3 mt-3">
                <!-- Qty control -->
                <div class="flex items-center border border-gray-300 rounded-full overflow-hidden bg-gray-50">
                  <button
                    @click.stop="changeQty(index, -1)"
                    class="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors text-lg font-bold"
                  >−</button>
                  <span class="px-3 font-semibold text-gray-800 min-w-[2rem] text-center">{{ item.quantity }}</span>
                  <button
                    @click.stop="changeQty(index, 1)"
                    class="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors text-lg font-bold"
                  >+</button>
                </div>

                <!-- Delete -->
                <button
                  @click.stop="removeItem(index)"
                  class="text-red-400 hover:text-red-600 text-xs font-semibold flex items-center gap-1 transition-colors"
                >
                  <i class="fa-solid fa-trash-can"></i> Remove
                </button>

                <!-- Line subtotal -->
                <span class="ml-auto text-sm font-bold text-gray-700">
                  ${{ (item.price * item.quantity).toFixed(2) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Order Summary Panel ─────────────────────────────────────── -->
        <div class="lg:w-80 shrink-0">
          <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-6">
            <h2 class="font-bold text-lg text-gray-800 mb-4 border-b pb-3">Order Summary</h2>

            <div class="space-y-2 text-sm text-gray-600 mb-4">
              <div class="flex justify-between">
                <span>Items ({{ store.cartNumber }})</span>
                <span class="font-semibold text-gray-800">${{ store.cartTotal }}</span>
              </div>
              <div class="flex justify-between">
                <span>Shipping</span>
                <span class="text-green-600 font-semibold">FREE</span>
              </div>
              <div class="flex justify-between text-xs text-gray-400">
                <span>Estimated Tax</span>
                <span>—</span>
              </div>
            </div>

            <div class="flex justify-between font-bold text-base text-gray-900 border-t pt-3 mb-5">
              <span>Order Total</span>
              <span class="text-green-700 text-lg">${{ store.cartTotal }}</span>
            </div>

            <button
              @click="checkout"
              class="w-full bg-yellow-400 hover:bg-yellow-500 active:scale-95 text-gray-900 font-bold py-3 rounded-full shadow transition-all duration-150 text-base"
            >
              Proceed to Checkout
            </button>

            <button
              @click="clearAll"
              class="w-full mt-3 border border-red-300 text-red-400 hover:bg-red-50 font-semibold py-2 rounded-full text-sm transition-colors"
            >
              Clear Cart
            </button>

            <router-link
              to="/"
              class="block text-center text-green-700 hover:underline font-semibold text-sm mt-4"
            >
              ← Continue Shopping
            </router-link>
          </div>
        </div>

      </div>
    </div>

    <!-- ── Footer ──────────────────────────────────────────────────────── -->
    <div class="mt-12">
      <div class="bg-gray-600 text-white text-center py-3 cursor-pointer hover:bg-gray-500 transition-colors" @click="scrollTop">
        <div class="text-xs"><i class="fa-solid fa-caret-up"></i></div>
        <div class="text-sm font-semibold">TOP OF PAGE</div>
      </div>

      <div class="bg-gray-800 text-white py-8 px-5">
        <div class="max-w-screen-xl mx-auto grid grid-cols-2 sm:grid-cols-3 gap-8">
          <div>
            <h3 class="font-bold mb-3 text-gray-300">Get to Know Us</h3>
            <ul class="space-y-2 text-sm text-gray-400">
              <li><a href="" class="hover:underline">Amazon.com</a></li>
              <li><a href="" class="hover:underline">Your Lists</a></li>
              <li><a href="" class="hover:underline">Your Account</a></li>
              <li><a href="" class="hover:underline">Customer Service</a></li>
            </ul>
          </div>
          <div>
            <h3 class="font-bold mb-3 text-gray-300">Manage</h3>
            <ul class="space-y-2 text-sm text-gray-400">
              <li><a href="" class="hover:underline">Your Orders</a></li>
              <li><a href="" class="hover:underline">Gift Cards</a></li>
              <li><a href="" class="hover:underline">Your Returns</a></li>
            </ul>
          </div>
        </div>
      </div>

      <div class="bg-gray-900 text-white py-8 text-center text-xs text-gray-400 space-y-2">
        <div class="flex justify-center gap-4 flex-wrap">
          <a href="" class="hover:underline">Conditions of Use</a>
          <a href="" class="hover:underline">Privacy Notice</a>
        </div>
        <div>&copy;1996-2026, Amazon.com, Inc. or its affiliates</div>
      </div>
    </div>

    <!-- ── Checkout success toast ──────────────────────────────────────── -->
    <transition name="toast">
      <div
        v-if="showToast"
        class="fixed bottom-8 left-1/2 -translate-x-1/2 bg-green-600 text-white px-8 py-4 rounded-2xl shadow-2xl text-base font-semibold z-50 flex items-center gap-3"
      >
        <i class="fa-solid fa-circle-check text-xl"></i>
        Order placed successfully! 🎉
      </div>
    </transition>
  </div>
</template>

<script>
import Nav from '@/components/Nav.vue';
import { useEcommerceStore } from '@/stores/Ecommerce';

export default {
  components: { Nav },

  data() {
    return {
      store: null,
      showToast: false
    };
  },

  computed: {
    cartItems() {
      return this.store ? this.store.cartItems : [];
    }
  },

  mounted() {
    this.store = useEcommerceStore();
  },

  methods: {
    changeQty(index, delta) {
      this.store.updateQuantity(index, delta);
    },
    removeItem(index) {
      this.store.removeFromCartItem(index);
    },
    clearAll() {
      if (confirm('Are you sure you want to clear your cart?')) {
        this.store.clearCart();
      }
    },
    checkout() {
      this.showToast = true;
      setTimeout(() => {
        this.store.clearCart();
        this.showToast = false;
      }, 2500);
    },
    scrollTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Toast transition */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(2rem) scale(0.9);
}
</style>